/**
 * Display-time exercise name/instructions helper.
 *
 * Backend always returns name (English) + name_pt (Portuguese) on exercise docs.
 * This helper picks the right one based on the user's current language.
 */

/**
 * Get the display name for an exercise based on language.
 * @param {object} exercise - exercise object with name and optionally name_pt
 * @param {string} lang - "en" or "pt"
 * @returns {string} the display name
 */
export function exerciseName(exercise, lang = "en") {
  if (!exercise) return "";
  if (lang === "pt" && exercise.name_pt) return exercise.name_pt;
  return exercise.name || "";
}

/**
 * Get the display instructions for an exercise based on language.
 * @param {object} exercise - exercise object with instructions/instructions_list and optionally instructions_pt
 * @param {string} lang - "en" or "pt"
 * @returns {string} the display instructions
 */
export function exerciseInstructions(exercise, lang = "en") {
  if (!exercise) return "";
  if (lang === "pt" && exercise.instructions_pt) return exercise.instructions_pt;
  return exercise.instructions || "";
}

/* ──────────────────────────────────────────────────────────────────────────
   Cardio model — per-machine "profiles" (2026-05-12 overhaul).

   getExerciseInputMode → 'reps' | 'time' | 'cardio'
     reps   : kg + reps (default; includes no-suffix bodyweight plyometrics)
     time   : mm:ss — static holds + explicit "(TIME)" paired entries only
     cardio : whole minutes + 0-2 contextual metric columns from getCardioFields

   getCardioProfile(ex) → 'run' | 'treadmill' | 'bike' | 'elliptical'
                        | 'stepmill' | 'rower' | 'ergometer' | null
   Profiles are tested in a FIXED ORDER (treadmill before bike before elliptical
   …) so ambiguous names resolve deterministically.

   muscle_group === "Cardio" is intentionally NOT a signal (it over-matched
   burpees etc. — see v3.8.194).
   ────────────────────────────────────────────────────────────────────────── */

// Equipment values (from backend/routes/exercises.py CARDIO_MACHINE_EQUIPMENT_LIST)
// that pin a profile regardless of the exercise name.
const EQUIPMENT_PROFILE = {
  'Stationary Bike': 'bike',
  'Elliptical Machine': 'elliptical',
  'Stepmill Machine': 'stepmill',
  'Skierg Machine': 'rower',
  'Upper Body Ergometer': 'ergometer',
};

// Name regexes, evaluated in this order; first hit wins.
const PROFILE_NAME_TESTS = [
  ['treadmill', /\b(treadmill)\b|run\s*\(treadmill\)|run\s*\(equipment\)|walking on incline treadmill/i],
  ['bike',      /\b(stationary bike|exercise bike|upright bike|recumbent bike|spin bike|cycle cross)\b/i],
  ['elliptical',/\b(elliptical|cross trainer)\b/i],
  ['stepmill',  /\b(stepmill|step mill|stair climber|stairmaster|stairmill)\b/i],
  ['rower',     /\b(rowing machine|rower|skierg|ski erg|ski-erg)\b/i],
  ['ergometer', /\b(ergometer|hand bike|arm bike|hand cycle|arm ergometer)\b/i],
  ['run',       /^(run|sprint|jog)\b|\b(run|sprint|jog)$/i],
];

// Static-hold names (no machine readout) → 'time' (mm:ss). Order doesn't matter.
const HOLD_NAME_REGEX = /\b(plank|wall sit|march sit|dead hang|l-?sit|hollow hold|boat hold|bird dog hold|hang hold|isometric hold|side hold)\b/i;

/**
 * Returns 'reps' | 'time' | 'cardio'. Detection priority:
 *   1. explicit "(TIME)" / "(REPS)" suffix on the name
 *   2. a cardio profile matches (equipment first, then name)  → 'cardio'
 *   3. a static-hold name matches                              → 'time'
 *   4. default                                                 → 'reps'
 */
export function getExerciseInputMode(exercise) {
  if (!exercise) return 'reps';
  const name = exercise.name || '';
  if (/\(TIME\)\s*$/i.test(name)) return 'time';
  if (/\(REPS\)\s*$/i.test(name)) return 'reps';
  if (getCardioProfile(exercise)) return 'cardio';
  if (HOLD_NAME_REGEX.test(name)) return 'time';
  return 'reps';
}

/** The cardio profile of an exercise, or null if it isn't a continuous-cardio
 *  exercise. Does NOT consider the "(TIME)"/"(REPS)" suffixes — callers that
 *  care use getExerciseInputMode first. */
export function getCardioProfile(exercise) {
  if (!exercise) return null;
  const name = exercise.name || '';
  // A "(TIME)"/"(REPS)" paired entry is never a cardio-profile exercise.
  if (/\((TIME|REPS)\)\s*$/i.test(name)) return null;
  const byEquip = EQUIPMENT_PROFILE[exercise.equipment];
  if (byEquip) return byEquip;
  for (const [profile, re] of PROFILE_NAME_TESTS) {
    if (re.test(name)) return profile;
  }
  return null;
}

// Field descriptors. toDisplay/fromInput translate between the stored value and
// what the <Input> shows (km↔m, plain ints/floats). headerKey is an i18n key.
const FIELD_FLOAT = (key, headerKey, placeholder, step) => ({
  key, headerKey, placeholder, step, htmlInputMode: 'decimal',
  toDisplay: (v) => (v ? v : ''),
  fromInput: (v) => (v === '' ? 0 : parseFloat(v) || 0),
});
const FIELD_INT = (key, headerKey, placeholder, step) => ({
  key, headerKey, placeholder, step, htmlInputMode: 'numeric',
  toDisplay: (v) => (v ? v : ''),
  fromInput: (v) => (v === '' ? 0 : parseInt(v, 10) || 0),
});
const FIELD_DISTANCE_KM = {
  key: 'distance_m', headerKey: 'workout.colDistance', placeholder: 'km', step: '0.1',
  unit: 'km', htmlInputMode: 'decimal',
  toDisplay: (m) => (m ? Math.round(m) / 1000 : ''),
  fromInput: (v) => (v === '' ? 0 : Math.round((parseFloat(v) || 0) * 1000)),
};
const FIELD_DISTANCE_M = {
  ...FIELD_INT('distance_m', 'workout.colDistance', 'm', '1'), unit: 'm',
};

const PROFILE_FIELDS = {
  run:        [FIELD_DISTANCE_KM],
  treadmill:  [FIELD_FLOAT('speed_kmh', 'workout.colSpeed', 'km/h', '0.1'),
               FIELD_FLOAT('incline_pct', 'workout.colIncline', '%', '0.5')],
  bike:       [FIELD_INT('resistance_level', 'workout.colLevel', 'lvl', '1')],
  elliptical: [FIELD_INT('resistance_level', 'workout.colLevel', 'lvl', '1'),
               FIELD_FLOAT('incline_pct', 'workout.colIncline', '%', '0.5')],
  stepmill:   [FIELD_INT('resistance_level', 'workout.colLevel', 'lvl', '1')],
  rower:      [FIELD_DISTANCE_M],
  ergometer:  [FIELD_INT('resistance_level', 'workout.colLevel', 'lvl', '1')],
};

/** Ordered metric-column descriptors for a cardio exercise (after the MIN
 *  column). Empty array for non-cardio exercises. The returned array is a fresh
 *  reference each call — callers that pass it across a memo boundary must
 *  memoize it themselves (keyed on exercise.name + exercise.equipment). */
export function getCardioFields(exercise) {
  const profile = getCardioProfile(exercise);
  if (!profile) return [];
  return PROFILE_FIELDS[profile] || [];
}

/** Default number of sets to create when an exercise is first added. Continuous
 *  cardio → 1 (nobody does 3 "sets" of a treadmill run). Everything else → 3. */
export function defaultSetCount(exercise) {
  return getExerciseInputMode(exercise) === 'cardio' ? 1 : 3;
}

// Backwards-compat shims for the dozens of existing "is this kg/reps?" gates.
export function isTimeBasedExercise(exercise) {
  return getExerciseInputMode(exercise) !== 'reps';
}
export function isMachineCardioExercise(exercise) {
  return getExerciseInputMode(exercise) === 'cardio';
}

// Format a duration in seconds as "MM:SS" (or "H:MM:SS" past 1h). Falsy → "-".
export function formatDuration(seconds) {
  const total = Math.max(0, parseInt(seconds, 10) || 0);
  if (!total) return '-';
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  const pad = (n) => String(n).padStart(2, '0');
  if (h > 0) return `${h}:${pad(m)}:${pad(s)}`;
  return `${pad(m)}:${pad(s)}`;
}

/** Compact one-line summary of a logged cardio set, e.g. "35 min · 6.2 km",
 *  "30 min · 9 km/h · 4%", "30 min · level 12". Used by PR cards, story
 *  generators, history rows. Returns "-" when nothing is logged. */
export function formatCardioSetSummary(set, exercise) {
  if (!set) return '-';
  const mins = Math.round((parseInt(set.duration_seconds, 10) || 0) / 60);
  const parts = [];
  if (mins) parts.push(`${mins} min`);
  for (const f of getCardioFields(exercise)) {
    const raw = set[f.key];
    if (!raw) continue;
    if (f.key === 'distance_m') {
      parts.push(f.unit === 'km' ? `${Math.round(raw) / 1000} km` : `${raw} m`);
    } else if (f.key === 'resistance_level') {
      parts.push(`level ${raw}`);
    } else if (f.key === 'speed_kmh') {
      parts.push(`${raw} km/h`);
    } else if (f.key === 'incline_pct') {
      parts.push(`${raw}%`);
    }
  }
  return parts.length ? parts.join(' · ') : '-';
}
